import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { t } from "ttag";

import { forgotPassword } from "@/app/api/legacy-api";
import { ROUTES } from "@/app/router";
import { Logo } from "@/assets";
import { emailSchema } from "@/shared/models/legacy-schemas";
import {
  Button,
  CircleIcon,
  Form,
  Heading,
  Input,
  LetterIcon,
  PlainIcon,
  Text,
} from "@/shared/ui";

interface ErrorType {
  response: { data: { error: { message: string } } };
}

export const ForgotPassword = () => {
  const navigate = useNavigate();

  const {
    mutate: forgotPasswordMutation,
    isPending,
    isSuccess,
  } = useMutation({
    mutationFn: forgotPassword.mutation,
    onError: (error: ErrorType) => {
      form.setError("email", {
        type: "manual",
        message: error.response.data.error.message,
      });
    },
  });

  const form = useForm({
    resolver: zodResolver(emailSchema),
  });

  const handleSubmit = form.handleSubmit((values) =>
    forgotPasswordMutation(values),
  );

  return (
    <>
      <header className="flex w-full flex-col gap-16">
        <Logo className="h-8 sm:h-11" />

        {!isSuccess && (
          <div className="flex flex-col items-center gap-2">
            <Heading size="medium">{t`Forgot password?`}</Heading>

            <Text align="center">
              {t`Please enter your registered email address.`}
              <br />
              {t`We will send you a link to reset your password.`}
            </Text>
          </div>
        )}

        {isSuccess && (
          <div className="flex w-full flex-col items-center gap-6">
            <CircleIcon size="lg" icon={PlainIcon} />

            <div className="flex flex-col items-center gap-2">
              <Heading size="medium">{t`Email sent`}</Heading>

              <Text align="center">
                {t`Check your inbox! If an account is registered under that email, you will receive a link to reset your password.`}
              </Text>
            </div>

            <Button
              size="lg"
              onClick={() => navigate(ROUTES.LOGIN)}
              className="w-full"
            >
              {t`Back to Log in`}
            </Button>
          </div>
        )}
      </header>

      {!isSuccess && (
        <Form.Provider {...form}>
          <form onSubmit={handleSubmit} className="flex w-full flex-col gap-16">
            <Form.Field
              control={form.control}
              name="email"
              render={() => (
                <Form.Item>
                  <Form.Label>{t`Email`}</Form.Label>
                  <Form.Control>
                    <Input.Root
                      {...form.register("email")}
                      autoComplete="email"
                      placeholder={t`email@example.com`}
                    >
                      <Input.Slot>
                        <LetterIcon />
                      </Input.Slot>
                    </Input.Root>
                  </Form.Control>
                  <Form.Message compact={false} />
                </Form.Item>
              )}
            />

            <div className="flex flex-col gap-4">
              <Button
                type="submit"
                className="w-full"
                size="lg"
                disabled={isPending}
              >
                {t`Send email`}
              </Button>

              <Button
                size="lg"
                variant="secondary"
                onClick={() => navigate(ROUTES.LOGIN)}
                className="w-full"
              >
                {t`Back`}
              </Button>
            </div>
          </form>
        </Form.Provider>
      )}
    </>
  );
};
